/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import { EnhancedSectionData } from '../../types/editor';
import FreeElement from './FreeElement';

interface FreeCanvasProps {
  sections: EnhancedSectionData[];
  selectedId: string | null;
  canvasWidth: number;
  canvasHeight: number;
  onSelectElement: (id: string | null) => void;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, x: number, y: number, width: number, height: number) => void;
  onRotate: (id: string, rotation: number) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, content: string, extra?: any) => void;
  onBringForward: (id: string) => void;
  onSendBackward: (id: string) => void;
  placeholders?: Array<{ value: string; label: string }>;
  isDraggingFromSidebar?: boolean;
  watermark?: {
    show: boolean;
    opacity: number;
    size: number;
    rotation: number;
    image?: string;
  };
  onMoveToPage?: (id: string, offset: -1 | 1) => void;
  pageIndex?: number;
  totalPages?: number;
}

const FreeCanvas: React.FC<FreeCanvasProps> = ({
  sections,
  selectedId,
  canvasWidth,
  canvasHeight,
  onSelectElement,
  onMove,
  onResize,
  onRotate,
  onDelete,
  onUpdate,
  onBringForward,
  onSendBackward,
  placeholders,
  isDraggingFromSidebar,
  watermark,
  onMoveToPage,
  pageIndex,
  totalPages,
}) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const { setNodeRef } = useDroppable({ id: 'main-canvas' });

  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    canvasRef.current = node;
  }, [setNodeRef]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    // Deselect if clicking on the canvas background
    if (e.target === canvasRef.current) {
      onSelectElement(null);
    }
  }, [onSelectElement]);

  const isEmpty = sections.filter(s => !s.hidden).length === 0;

  return (
    <Box
      ref={mergedRef}
      onClick={handleCanvasClick}
      data-free-canvas="true"
      style={{
        width: `${canvasWidth}px`,
        minWidth: `${canvasWidth}px`,
        maxWidth: `${canvasWidth}px`,
        minHeight: `${canvasHeight}px`,
        height: '100%',
      }}
      sx={{
        position: 'relative',
        width: `${canvasWidth}px !important`,
        minWidth: `${canvasWidth}px !important`,
        maxWidth: `${canvasWidth}px !important`,
        height: '100%',
        minHeight: `${canvasHeight}px !important`,
        flex: 1,
        bgcolor: '#fff',
        overflow: 'hidden',
        outline: isDraggingFromSidebar ? '2px dashed #6366f1' : 'none',
        outlineOffset: '-2px',
        transition: 'outline 0.15s ease',
        borderRadius: 0,
      }}
    >
      {/* Watermark overlay behind elements */}
      {watermark?.show && watermark?.image && (
        <Box
          className="st-wm"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <img
            src={watermark.image}
            alt="Watermark"
            style={{
              opacity: (watermark.opacity ?? 15) / 100,
              width: `${watermark.size ?? 50}%`,
              transform: `rotate(${watermark.rotation ?? 0}deg)`,
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        </Box>
      )}

      {/* Empty state hint */}
      {isEmpty && !isDraggingFromSidebar && !watermark?.show && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#f0f0ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
            }}
          >
            🎨
          </Box>
          <Box sx={{ color: '#9ca3af', fontSize: 14, fontWeight: 500, textAlign: 'center', lineHeight: 1.5 }}>
            Drag elements from the sidebar<br />to place them anywhere on the canvas
          </Box>
        </Box>
      )}

      {/* Drop target hint while dragging */}
      {isEmpty && isDraggingFromSidebar && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            bgcolor: 'rgba(99, 102, 241, 0.04)',
          }}
        >
          <Box sx={{ color: '#6366f1', fontSize: 15, fontWeight: 600 }}>
            Drop here to place element
          </Box>
        </Box>
      )}

      {/* Free-positioned elements */}
      {sections
        .filter(s => !s.hidden)
        .sort((a, b) => (a.zIndex ?? 1) - (b.zIndex ?? 1))
        .map(section => (
          <FreeElement
            key={section.id}
            element={section}
            isSelected={selectedId === section.id}
            canvasBounds={{ width: canvasWidth, height: canvasHeight }}
            onSelect={() => onSelectElement(section.id)}
            onMove={onMove}
            onResize={onResize}
            onRotate={onRotate}
            onDelete={onDelete}
            onUpdate={onUpdate}
            onBringForward={onBringForward}
            onSendBackward={onSendBackward}
            placeholders={placeholders}
            onMoveToPage={onMoveToPage}
            pageIndex={pageIndex}
            totalPages={totalPages}
          />
        ))}
    </Box>
  );
};

export default FreeCanvas;
